/* eslint-disable no-console */
/* eslint-disable react/require-default-props */
/**
 * 申請人資料填寫/查看
 */
import React, { useEffect } from 'react';
import { LayoutForm as Form, Select, CheckBoxGroup, Text } from '@/components/BaseForm';
import { useIntl, useSelector } from 'umi';
import type { FormInstance } from 'antd';
import { MoAddress, MoName, MoDatePicker, MoPhone, MoGender } from '@gov-mo/components';
import type { PersonalFormType } from '../../types/defs';
import styles from './PersonalForm.less';
import Upload from '../Upload';

export interface PersonalFormProps extends React.HTMLAttributes<HTMLDivElement> {
  isDetail?: boolean;
  onInstance?: (instance: FormInstance | null) => void;
  mobile?: boolean;
  values?: PersonalFormType & Record<string, unknown>;
}

export interface FormValues {
  formFieldA: { label: string; value: string }[];
  formFieldB?: string;
  formFieldC: number[];
}

const PersonalForm = (props: PersonalFormProps) => {
  const intl = useIntl();
  const { brandColor } = useSelector((x) => x.public);
  const { onInstance, isDetail = false, values, mobile = true } = props;
  const [form] = Form.useForm();

  useEffect(() => {
    onInstance?.({
      ...form,
      validateFields: () =>
        form.validateFields().then((vals: FormValues) => ({
          ...vals,
          formFieldA: vals.formFieldA[0]?.value,
          formFieldC: vals.formFieldC,
        })),
      setFieldsValue: (vals: PersonalFormType) => {
        form.setFieldsValue({
          ...vals,
          formFieldA: vals.formFieldA ? [vals.formFieldA] : undefined,
          formFieldC: vals.formFieldC,
        });
      },
    });
    form.setFieldsValue({
      ...values,
      formFieldA: values?.formFieldA ? [values.formFieldA] : undefined,
    });
  }, []);

  // 詳情顯示內容
  if (isDetail) {
    return (
      <div className={`${mobile ? styles.detailRoot : styles.webDetailRoot}`}>
        {/* 預置鍵測試 */}
        <div role="text">
          <span>{intl.formatMessage({ id: '姓名' })}</span>
          <span>{`${values?.firstName}-${values?.lastName}`}</span>
        </div>

        <div role="text">
          <span>{intl.formatMessage({ id: '日期選擇' })}</span>
          <span>{values?.datepicker?.format('YYYY-MM-DD')}</span>
        </div>

        <div role="text">
          <span>{intl.formatMessage({ id: '電話' })}</span>
          <span>{`${values?.phone?.area}-${values?.phone?.number}`}</span>
        </div>

        <div role="text">
          <span>{intl.formatMessage({ id: '性別' })}</span>
          <span>{values?.gender === 'M' ? '女' : '男'}</span>
        </div>

        {/* 文件模組測試 */}
        <div role="text">
          <span>{intl.formatMessage({ id: '文件上传' })}</span>
          <span>
            <Upload id="com0" value={values?.formFieldUpload} isDetail />
          </span>
        </div>

        {/* 地址模組測試 */}
        <div role="text">
          <span>{intl.formatMessage({ id: '地址模組' })}</span>
          <span>{values?.formFieldAddress}</span>
        </div>

        {/* 自定義表單組件測試 */}
        <div role="text">
          <span>{intl.formatMessage({ id: '下拉表單項' })}</span>
          <span>{values?.formFieldA}</span>
        </div>
        {values?.formFieldA === '選項2的值' && (
          <div role="text">
            <span>{intl.formatMessage({ id: '連動選項' })}</span>
            <span>{values.formFieldB}</span>
          </div>
        )}
        <div role="text">
          <span>{intl.formatMessage({ id: '複選表單項' })}</span>
          <div className={styles.prewrapDiv}>
            {values?.formFieldC?.filter((i) => i)?.join('\n')}
          </div>
        </div>
      </div>
    );
  }

  // 編輯顯示內容
  return (
    <>
      {mobile && <div className={styles.baseTitleContainer} />}
      <Form form={form} className={styles.root}>
        {/* 預置鍵測試 */}
        <MoName />
        <MoDatePicker />
        <MoPhone />
        <MoGender
          formItemProps={{
            initialValue: 'M',
          }}
        />

        {/* 文件模組測試 */}
        <Form.Item label={intl.formatMessage({ id: '文件上传' })} name="formFieldUpload">
          <Upload id="com0" />
        </Form.Item>

        {/* 地址模組測試 */}
        <Form.Item
          label={intl.formatMessage({ id: '地址模組' })}
          required
          rules={[{ required: true }]}
          name="formFieldAddress"
        >
          <MoAddress
            brandColor={brandColor}
            onChange={(addrStr, addObj) => {
              console.log('🚀 ~ PersonalForm ~ addrStr, addObj:', addrStr, addObj);
            }}
            onStreetChange={(streetData) => {
              console.log('🚀 ~ PersonalForm ~ streetData:', streetData);
            }}
            onResAddrSplit={(addrObj) => {
              console.log('🚀 ~ PersonalForm ~ addrObj:', addrObj);
            }}
          />
        </Form.Item>

        {/* 自定義表單組件測試 */}
        <Form.Item
          label={intl.formatMessage({ id: '下拉表單項' })}
          required
          rules={[{ required: true, message: intl.formatMessage({ id: '请选择' }) }]}
          name="formFieldA"
        >
          <Select
            options={[
              { label: '選項1', value: '選項1的值' },
              { label: '選項2', value: '選項2的值' },
            ]}
            placeholder={intl.formatMessage({ id: '请选择' })}
          />
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prev: FormValues, cur: FormValues) => prev.formFieldA !== cur.formFieldA}
        >
          {({ getFieldValue }) =>
            // 當useCode=2的時，顯示這個表單項
            getFieldValue('formFieldA')?.[0]?.value === '選項2的值' ? (
              <Form.Item
                name="formFieldB"
                label={intl.formatMessage({ id: '連動選項' })}
                required
                rules={[{ required: true, message: intl.formatMessage({ id: '请输入' }) }]}
              >
                <Text placeholder={intl.formatMessage({ id: '请输入' })} maxLength={256} />
              </Form.Item>
            ) : null
          }
        </Form.Item>
        <Form.Item
          label={intl.formatMessage({ id: '複選表單項（可多選）' })}
          required
          rules={[{ required: true }]}
          name="formFieldC"
          initialValue={['複選值1']}
        >
          <CheckBoxGroup
            multiple
            options={[
              { value: '複選值1', label: '這是一個復選框的選項1內容.....' },
              { value: '複選值2', label: '這是一個復選框的選項2內容.....' },
              { value: '複選值3', label: '這是一個復選框的選項3內容.....' },
            ]}
            layout="vertical"
          />
        </Form.Item>
      </Form>
    </>
  );
};

export default PersonalForm;
